// src/components/lesson/diagrams/ParallelLines.tsx
//
// Two parallel lines cut by a transversal (the classic SAT angle figure:
// corresponding, alternate-interior/exterior, co-interior angles), or — with
// parallel:false — two intersecting lines (vertical / supplementary angles).
// Angle labels are placed in the four quadrants around each intersection.
//
// Params (all optional):
//   parallel?: boolean              // default true (two || lines); false = one intersection
//   lineLabels?: { top?, bottom? }  // name the lines, e.g. { top: "m", bottom: "n" }
//   transversalLabel?: string       // name the transversal, e.g. "t"
//   angles?: { at?: 'top'|'bottom', pos: 'TL'|'TR'|'BL'|'BR', label: string }[]
//   label?: string

type Pos = 'TL' | 'TR' | 'BL' | 'BR'
type AngleLabel = { at?: 'top' | 'bottom'; pos: Pos; label: string }

type ParallelLinesParams = {
  parallel?: boolean
  lineLabels?: { top?: string; bottom?: string }
  transversalLabel?: string
  angles?: AngleLabel[]
  label?: string
}

const W = 340
const H = 240
const X0 = 28
const X1 = W - 28

const SHAPE = 'rgb(148 163 184)'
const TRANS = 'rgb(34 211 238)'
const ANGLE = 'rgb(251 191 36)'

// Quadrant offset (into the quadrant) from an intersection point.
const QOFF: Record<Pos, { x: number; y: number }> = {
  TL: { x: -24, y: -16 },
  TR: { x: 24, y: -16 },
  BL: { x: -24, y: 20 },
  BR: { x: 24, y: 20 },
}

export default function ParallelLines({ params }: { params: Record<string, unknown> }) {
  const p = params as ParallelLinesParams
  const parallel = p.parallel !== false
  const angles = Array.isArray(p.angles) ? p.angles : []

  const topY = parallel ? 78 : H / 2
  const botY = parallel ? 170 : H / 2
  // Transversal crosses the top line left of where it crosses the bottom line.
  const topX = 150
  const botX = 205
  const T = { x: topX, y: topY }
  const B = { x: botX, y: botY }
  // Extend the transversal a bit past both intersections.
  const dx = botX - topX
  const dy = botY - topY
  const ext = 0.4
  const tStart = { x: topX - dx * ext, y: topY - dy * ext }
  const tEnd = { x: botX + dx * ext, y: botY + dy * ext }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={
        p.label ?? (parallel ? 'Parallel lines cut by a transversal' : 'Intersecting lines')
      }
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Top horizontal line */}
      <line x1={X0} y1={topY} x2={X1} y2={topY} stroke={SHAPE} strokeWidth="1.5" />
      {p.lineLabels?.top && (
        <text x={X1 - 2} y={topY - 6} fontSize="12" textAnchor="end" fill={SHAPE}>
          {p.lineLabels.top}
        </text>
      )}

      {/* Bottom line (only a distinct line when parallel) */}
      {parallel && (
        <>
          <line x1={X0} y1={botY} x2={X1} y2={botY} stroke={SHAPE} strokeWidth="1.5" />
          {p.lineLabels?.bottom && (
            <text x={X1 - 2} y={botY - 6} fontSize="12" textAnchor="end" fill={SHAPE}>
              {p.lineLabels.bottom}
            </text>
          )}
        </>
      )}

      {/* Transversal */}
      <line x1={tStart.x} y1={tStart.y} x2={tEnd.x} y2={tEnd.y} stroke={TRANS} strokeWidth="1.5" />
      {p.transversalLabel && (
        <text x={tEnd.x + 4} y={tEnd.y + 4} fontSize="12" fill={TRANS}>
          {p.transversalLabel}
        </text>
      )}

      {/* Intersection dots */}
      <circle cx={T.x} cy={T.y} r="2.5" fill={SHAPE} />
      {parallel && <circle cx={B.x} cy={B.y} r="2.5" fill={SHAPE} />}

      {/* Angle labels */}
      {angles.map((a, i) => {
        const at = parallel ? (a.at ?? 'top') : 'top'
        const origin = at === 'bottom' ? B : T
        const off = QOFF[a.pos] ?? QOFF.TR
        return (
          <text
            key={`a-${i}`}
            x={origin.x + off.x}
            y={origin.y + off.y}
            fontSize="12"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={ANGLE}
          >
            {a.label}
          </text>
        )
      })}
    </svg>
  )
}
